import { util } from "./util";
import { Cache } from "./Cache";
import { sendEventToTracking } from "./tracking";
import type { SquirrelEvents, TrackingData } from "./types/tracking";
import {
  MARK,
  HEADLINE,
  SELECTOR,
  DATA,
  KEY_CODE,
  encode,
  getNextActiveElement,
  CLASSNAME,
  extractTrackparameterFromUrl,
  splitTrackingLabelsFromUrl,
} from "./helper";
import { suggestTracking } from "./suggestTracking";
import { AttributeResponse, Keyword, Suggestions } from "./helper";
import Mustache from "mustache";
import { TrackEmptyState, trackEmptyState } from "./tracking/EmptyState";
import { trackSuggestTime } from "./tracking/Suggest";
import { FALLBACK_SEARCH_URL, ErrorMessage } from "./types/constants";
import { Queue } from "./PromiseQueue";
/*🤫*/
/*🤫*/
import templateString from "../fragment/searchSuggest.mustache";

export const TEMPLATE_NAME = "searchSuggest";

let headerElement: Element | null;
let searchbarWrapper: HTMLDivElement | null;
let searchForm: HTMLFormElement | null;
let searchField: HTMLInputElement | null;
let suggestLayer: HTMLDivElement | null;
let oldSearchTerm: string | undefined | null;
let renderCache: Cache<Promise<string>> | null;

let isEmptyState: boolean;
let headline: string;

/*🤫
🤫
🤫
🤫*/
export function setOldSearchTerm(string: string) {
  oldSearchTerm = string;
}

export function focusOutSearchFieldAction(evt: Event) {
  trackEmptyState.measure();
}

/*🤫
🤫
🤫
🤫*/
export function initialMeasureSuggestBoxTime() {
  const suggestBoxTimePerformance: PerformanceEntryList = performance.getEntriesByName(MARK.INITIAL_SEARCH_BOX_TIME);
  const sessionStart = Number(window.o_util.cookie.get("o_squirrel_sessionStart"));
  if (suggestBoxTimePerformance.length == 0 && !!sessionStart) {
    performance.measure(MARK.INITIAL_SEARCH_BOX_TIME, {
      start: sessionStart,
      end: new Date().getTime(),
    });
  }
}

/*🤫
🤫
🤫
🤫
🤫*/
export function focusSearchFieldAction(evt: Event) {
  if (!headerElement) {
    throw new Error("searchSuggest.focusSearchFieldAction: headerElement is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.focusSearchFieldAction: suggestLayer is not defined");
  }

  performance.mark(MARK.SUGGEST_TIME_START);
  performance.mark(MARK.SUGGEST_RENDER_TIME_START);

  initialMeasureSuggestBoxTime();

  const selectedSearchField: HTMLInputElement = evt.target as HTMLInputElement;
  const value = selectedSearchField.value;
  const searchTerm: string = util.string.trimLeft(value);
  if (searchTerm !== "") {
    if (suggestLayer.innerHTML !== "" && !!oldSearchTerm) {
      showSuggestions(searchTerm);
    }
  } else {
    showEmptyState();
  }
}

/*🤫
🤫
🤫
🤫
🤫*/
export function clickArrowAction(this: Element, evt: Event) {
  if (!searchField) {
    throw new Error("searchSuggest.clickArrowAction: searchField is not defined");
  }
  if (!searchForm) {
    throw new Error("searchSuggest.clickArrowAction: searchForm is not defined");
  }

  const elem: HTMLElement = evt.target as HTMLElement;
  const target: ParentNode = (Boolean(this.parentNode).valueOf() ? this.parentNode : elem.offsetParent) as ParentNode; /*🤫*/
  const term: HTMLSpanElement = target.querySelector(SELECTOR.SEARCH_TERM) as HTMLSpanElement;
  const searchTerm: string | null = term.getAttribute(DATA.TERM);
  const position: string | null = term.getAttribute(DATA.POS);

  window.o_util.event.stop(evt);

  setSuggest(position);
  replaceSearchTermAndSetDataAttribute(term.getAttribute(DATA.TERM) + " ");

  sendEventToTracking({
    san_SuggestAdoptTerm: searchTerm,
    san_SuggestAdoptPosition: position,
  });

  suggestTracking.unmarkFormForTracking(CLASSNAME.TRACK_SUGGEST, searchForm);
  suggestTracking.markFormForTracking(CLASSNAME.TRACK_MODIFIED, searchForm);

  searchField.focus();
}

/*🤫
🤫
🤫
🤫*/
export function hideLayerEventHandler(evt: MouseEvent) {
  const elem: HTMLElement = evt.target as HTMLElement;
  if (!elem.classList.contains(CLASSNAME.IGNORE_CLOSE_SUGGEST)) {
    hideLayer();
  }
}

/*🤫
🤫
🤫
🤫*/
export function updateSuggestionLayer<E extends HTMLDivElement>(this: E, suggestions: string): E {
  performance.measure(MARK.SUGGEST_RENDER_TIME, MARK.SUGGEST_RENDER_TIME_START);
  this.innerHTML = suggestions;
  return this;
}

/*🤫
🤫
🤫
🤫
🤫*/
export function clickSearchTermAction(this: Element, evt: Event) {
  if (!searchForm) {
    throw new Error("searchSuggest.clickSearchTermAction: searchForm is not defined");
  }
  if (!searchField) {
    throw new Error("searchSuggest.clickSearchTermAction: searchField is not defined");
  }

  const elem: HTMLElement = evt.target as HTMLElement;
  const target: ParentNode = (Boolean(this.querySelector).valueOf() ? this : elem.offsetParent) as ParentNode;
  const term: HTMLDivElement = target.querySelector(SELECTOR.SEARCH_TERM) as HTMLDivElement;

  window.o_util.event.stop(evt);

  setSuggest(term.getAttribute(DATA.POS));
  const originalSearchTerm: AttributeResponse = searchField.getAttribute(DATA.SUGGEST_SEARCH_TERM);
  replaceSearchTermAndSetDataAttribute(term.getAttribute(DATA.TERM));

  const suggestTime: number = trackSuggestTime.getCurrentTime();

  const targetUrl: string = this.children[0].getAttribute(DATA.TARGET) as string;
  const [url, tValue] = splitTrackingLabelsFromUrl(targetUrl);
  const hashTrackingParameters: TrackingData = extractTrackparameterFromUrl(tValue);

  const trackingParameters: TrackingData = {};
  const isSuggest = !!originalSearchTerm;
  if (isSuggest) {
    trackingParameters["wk.san_SuggestTime"] = suggestTime;
  }

  suggestTracking.trackUsage(trackingParameters, hashTrackingParameters, false, true, isEmptyState, originalSearchTerm);

  suggestTracking.unmarkFormForTracking(CLASSNAME.TRACK_MODIFIED, searchForm);
  submitForm(url);
}

/*🤫
🤫
🤫
🤫
🤫
🤫*/
export function retrieveSuggestions(searchTerm: string): Promise<string> {
  return Promise.all([
    fetchSuggestions(searchTerm).then(extractSuggestions).then(updateSearchFieldTarget),
    Promise.resolve(templateString),
  ]).then(renderSuggestions);
}

/*🤫
🤫
🤫
🤫*/
export function fetchSuggestions(searchTerm: string): Promise<Suggestions> {
  if (!suggestLayer) {
    throw new Error("searchSuggest.fetchSuggestions: suggestLayer is not defined");
  }

  const url: string | null = suggestLayer.getAttribute(DATA.SUGGEST_URL);
  if (!url) {
    throw new Error("searchSuggest.fetchSuggestions: undefined suggest url");
  }

  return fetch(encode(url, { q: searchTerm })).then((response) => response.json());
}

/*🤫
🤫
🤫
🤫*/
export function extractSuggestions(json: Suggestions): Suggestions {
  const keywords: Keyword[] = json.keywords;
  return {
    original: json.original,
    keywords: keywords,
    suggestPos: util.core.counter(),
  };
}

export function updateSearchFieldTarget(response: false | Suggestions): Suggestions | Promise<unknown> {
  if (!searchField) {
    throw new Error("searchSuggest.extractSuggestions: searchField is not defined");
  }

  if (response && response.original && response.original.target) {
    const original: Keyword = response.original;
    searchField.setAttribute(DATA.TARGET, original.target);
    searchField.setAttribute(DATA.TARGET_TERM, original.term);
  }

  /*🤫*/
  if (response === false) {
    return Promise.reject(new Error(ErrorMessage.NO_OK_SUGGEST_RESPONSE));
  } else if (!response.keywords) {
    return Promise.reject(new Error(ErrorMessage.KEYWORDS_UNDEFINED));
  } else if (response.keywords.length === 0) {
    return Promise.reject(new Error(ErrorMessage.EMPTY_SUGGESTIONS));
  }
  return response;
}

/*🤫
🤫
🤫
🤫
🤫*/
export function renderSuggestions(results: [unknown, string]): string {
  if (!searchField) {
    throw new Error("searchSuggest.renderSuggestions: searchField is not defined");
  }
  const template: string = results[1] as string;
  const suggestions: Suggestions = results[0] as Suggestions;

  const view = Object.assign(suggestions, { isEmptyState, headline });
  performance.measure(MARK.SUGGEST_RENDER_TIME, MARK.SUGGEST_RENDER_TIME_START);
  return Mustache.render(template, view);
}

/*🤫
🤫
🤫*/
export function closeLayer() {
  window.o_global.events<SquirrelEvents>().emit("squirrel.search.close");
  hideLayer();
}

/*🤫
🤫
🤫*/
export function showLayer(): HTMLFormElement {
  if (!searchForm) {
    throw new Error("searchSuggest.showLayer: suggestLayer is not defined");
  }
  if (!searchbarWrapper) {
    throw new Error("searchSuggest.showLayer: searchWrapper is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.showLayer: suggestLayer is not defined");
  }

  document.body.addEventListener("click", hideLayerEventHandler);
  searchbarWrapper.classList.add(CLASSNAME.SEARCHBAR_WRAPPER_VISIBLE_SUGGESTIONS);
  suggestLayer.style.display = "block";
  return searchForm;
}

/*🤫
🤫
🤫*/
export function submitForm(action: string) {
  util.global.setLocation(action);
}

/*🤫
🤫
🤫
🤫
🤫
🤫*/
export function setSuggest(suggestPos?: string | number | undefined | null, redirectTarget?: string | null) {
  if (!searchForm) {
    throw new Error("searchSuggest.setSuggest: searchForm is not defined");
  }

  searchForm.setAttribute(DATA.POS, suggestPos ? (suggestPos as string) : "");
  if (redirectTarget) {
    searchForm.setAttribute(DATA.TARGET, redirectTarget);
  } else {
    searchForm.removeAttribute(DATA.TARGET);
  }
}

/*🤫
🤫
🤫
🤫
🤫*/
export function replaceSearchTerm(searchTerm: string | null) {
  if (!searchField) {
    throw new Error("searchSuggest.replaceSearchTerm: searchField is not defined");
  }
  if (searchTerm !== "" && searchTerm !== null) {
    searchField.value = searchTerm;
    /*🤫*/
    searchField.setAttribute(DATA.TARGET_TERM, searchTerm);
  } else {
    /*🤫*/
    searchField.removeAttribute(DATA.TARGET_TERM);
  }
}

export function replaceSearchFieldTarget(searchTerm: string | null, target: string | null) {
  if (!searchField) {
    throw new Error("searchSuggest.replaceSearchTerm: searchField is not defined");
  }
  if (target == null && searchTerm !== "" && searchTerm !== null) {
    target = FALLBACK_SEARCH_URL(searchTerm);
  }
  if (target) {
    searchField.setAttribute(DATA.TARGET, target);
  }
}

/*🤫
🤫
🤫
🤫
🤫*/
export function replaceSearchTermAndSetDataAttribute(searchTerm: string | null) {
  if (!searchField) {
    throw new Error("searchSuggest.replaceSearchTermAndSetDataAttribute: searchField is not defined");
  }
  if (searchTerm !== "" && searchTerm !== null) {
    searchField.value = searchTerm;
    searchField.setAttribute(DATA.SUGGEST_SEARCH_TERM, util.string.trimRight(searchTerm));
  }
}

/*🤫
🤫
🤫
🤫
🤫*/
export function suggest(searchTerm: string) {
  if (!renderCache) {
    throw new Error("searchSuggest.suggest: renderCache is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.suggest: suggestLayer is not defined");
  }
  isEmptyState = searchTerm == "";
  const suggestions = Queue
    /*🤫*/
    /*🤫*/
    .enqueue(() => renderCache.get(searchTerm))
    .then(window.o_util.core.bind(suggestLayer, updateSuggestionLayer));

  setSuggest(-1);
  suggestions.then(showLayer).catch(handleError);
}

/*🤫
🤫
🤫
🤫*/
export function updateSearchField(activeElement: Element) {
  if (!searchForm) {
    throw new Error("searchSuggest.updateSearchField: searchForm is not defined");
  }
  const termElement: HTMLElement = activeElement.querySelector(SELECTOR.SEARCH_TERM) as HTMLElement;
  const redirectTarget: AttributeResponse = termElement.getAttribute(DATA.TARGET);
  const term: AttributeResponse = termElement
    ? termElement.getAttribute(DATA.TERM)
    : activeElement.getAttribute(DATA.TERM);
  if (Boolean(termElement).valueOf()) {
    setSuggest(termElement.getAttribute(DATA.POS), redirectTarget);
  }
  replaceSearchTerm(term);
  replaceSearchFieldTarget(term, redirectTarget);
  suggestTracking.markFormForTracking(CLASSNAME.TRACK_SUGGEST, searchForm);
}

/*🤫
🤫
🤫
🤫
🤫*/
export function updateActiveElement(previous: HTMLLIElement | null, next: Element | null) {
  if (previous) {
    previous.classList.remove(CLASSNAME.SELECTED_SEARCH_SUGGEST);
  }
  if (next) {
    next.classList.add(CLASSNAME.SELECTED_SEARCH_SUGGEST);
    updateSearchField(next);
  }
}

/*🤫
🤫
🤫
🤫*/
export function getInitiallyActiveElement(key: string): HTMLLIElement | null {
  if (!suggestLayer) {
    throw new Error("searchSuggest.getInitiallyActiveElement: suggestLayer is not defined");
  }

  const entries: NodeListOf<HTMLLIElement> = suggestLayer.querySelectorAll(".squirrel_searchSuggest");
  switch (key) {
    case KEY_CODE.ARROW_DOWN:
      return entries[0];
    case KEY_CODE.ARROW_UP:
      return entries[entries.length - 1];
    default:
      return null;
  }
}

export function onFirstChar() {
  if (!searchField) {
    throw new Error("searchSuggest.onFirstChar: searchField is not defined");
  }

  if (!oldSearchTerm && !!searchField.value) {
    performance.mark(MARK.SUGGEST_TIME_START);
    TrackEmptyState.measure();
  }
}

/*🤫
🤫
🤫
🤫
🤫
🤫
🤫
🤫*/
export function handleKeyAction(evt: KeyboardEvent) {
  if (!searchForm) {
    throw new Error("searchSuggest.handleKeyAction: searchField is not defined");
  }
  if (!searchField) {
    throw new Error("searchSuggest.handleKeyAction: searchField is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.handleKeyAction: suggestLayer is not defined");
  }
  if (!headerElement) {
    throw new Error("searchSuggest.handleKeyAction: headerElement is not defined");
  }

  let searchTerm: string;
  let activeElement: HTMLLIElement;
  const key: string = Boolean(evt.which).valueOf() ? evt.which + "" : evt.code;
  switch (key) {
    case KEY_CODE.ARROW_UP:
    case KEY_CODE.ARROW_DOWN:
      evt.preventDefault();
      activeElement = suggestLayer.querySelector(SELECTOR.SELECTED_SEARCH_SUGGEST) as HTMLLIElement;
      updateActiveElement(
        activeElement,
        activeElement ? getNextActiveElement(key, activeElement) : getInitiallyActiveElement(key)
      );
      break;
    case KEY_CODE.ESC:
      closeLayer();
      break;
    default:
      performance.mark(MARK.SUGGEST_RENDER_TIME_START);
      onFirstChar();
      searchTerm = util.string.trimLeft(searchField.value);
      searchField.setAttribute(DATA.SUGGEST_SEARCH_TERM, searchTerm);
      /*🤫*/
      if (searchTerm !== "" && key !== KEY_CODE.ENTER) {
        if (oldSearchTerm !== searchTerm) {
          showSuggestions(searchTerm);
        }
        suggestTracking.unmarkFormForTracking(CLASSNAME.TRACK_SUGGEST, searchForm);
      } else {
        showEmptyState();
      }
      setOldSearchTerm(searchTerm);
  }
}

function showSuggestions(searchTerm: string) {
  setHeadline(HEADLINE.SUGGESTIONS);
  suggest(searchTerm);
}

function showEmptyState() {
  if (window.o_squirrel.enableEmptyState) {
    trackEmptyState.mark();
    setHeadline(HEADLINE.EMPTY_STATE);
    suggest("");
  } else {
    hideLayer();
  }
}

function setHeadline(label: string) {
  headline = label;
}

/*🤫
🤫
🤫*/
export function handleError() {
  setOriginalTarget();
  hideLayer();
}

function setOriginalTarget() {
  /*🤫*/
  /*🤫*/
  const fallback_url = FALLBACK_SEARCH_URL(searchField?.value);
  searchField?.setAttribute(DATA.TARGET, fallback_url);
}

/*🤫
🤫
🤫*/
export function hideLayer() {
  if (!searchbarWrapper) {
    throw new Error("searchSuggest.hideLayer: searchbarWrapper is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.hideLayer: suggestLayer is not defined");
  }
  if (!renderCache) {
    throw new Error("searchSuggest.hideLayer: renderCache is not defined");
  }
  document.body.removeEventListener("click", hideLayerEventHandler);
  searchbarWrapper.classList.remove(CLASSNAME.SEARCHBAR_WRAPPER_VISIBLE_SUGGESTIONS);
  suggestLayer.style.display = "none";
  suggestLayer.innerHTML = "";
  renderCache.clear();
  oldSearchTerm = undefined;
}

/*🤫
🤫
🤫*/
export function reset() {
  if (!searchField) {
    throw new Error("searchSuggest.reset: searchField is not defined");
  }
  if (!searchbarWrapper) {
    throw new Error("searchSuggest.reset: searchbarWrapper is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.reset: suggestLayer is not defined");
  }
  if (!renderCache) {
    throw new Error("searchSuggest.reset: renderCache is not defined");
  }
  searchField.value = "";
  hideLayer();
  searchbarWrapper = null;
  searchForm = null;
  searchField = null;
  suggestLayer = null;
  oldSearchTerm = "";
  renderCache.clear();
}

export function init() {
  headline = HEADLINE.SUGGESTIONS;

  const parentWrapper: HTMLDivElement = document.querySelector(SELECTOR.SEARCH_CONTAINER) as HTMLDivElement;

  renderCache = new Cache<Promise<string>>(retrieveSuggestions);
  if (!searchForm && parentWrapper) {
    searchbarWrapper = parentWrapper.querySelector(SELECTOR.SEARCH_WRAPPER) as HTMLDivElement;
    searchForm = parentWrapper.querySelector(SELECTOR.SEARCH_FORM) as HTMLFormElement;
    searchField = parentWrapper.querySelector(SELECTOR.SEARCH_FIELD) as HTMLInputElement;
    suggestLayer = parentWrapper.querySelector(SELECTOR.LAYER) as HTMLDivElement;
    headerElement = document.querySelector(SELECTOR.HEADER_ELEMENT) as HTMLElement;

    /*🤫*/
    if (suggestLayer) {
      window.o_util.event.delegate(SELECTOR.LAYER, "click", SELECTOR.SUGGEST_TERM, clickSearchTermAction);
      window.o_util.event.delegate(SELECTOR.LAYER, "click", SELECTOR.MODIFIED_ARROW, clickArrowAction);

      searchField.addEventListener("keyup", handleKeyAction);
      searchField.addEventListener("focus", focusSearchFieldAction);
      searchField.addEventListener("focusout", focusOutSearchFieldAction);
    }
  }
}

export const searchSuggest = { init, reset, setOldSearchTerm };
